/**
 * Recompensas de fin de run: monedas, XP y gear. Se otorgan completas al
 * ganar, y de forma parcial al perder si se superó al menos 1 oleada (spec
 * sección 3.6). Perder en la oleada 1 sin bajar a nadie no da nada.
 */

import type { ChapterDef, GearPiece, MetaState } from '../core/types';
import type { Rng } from '../core/rng';
import { addCoins, addXp } from './economy';
import { advanceChapterProgress } from './chapters';
import { rollGearDrops } from './gear';

export type RunResultOutcome = 'won' | 'lost';

export interface RunOutcome {
  /** Índice (0-indexado) del capítulo jugado dentro de `data/chapters.json`. */
  chapterIndex: number;
  outcome: RunResultOutcome;
  /** Cantidad de oleadas completamente superadas en esta run (incluye el boss si se ganó). */
  wavesCleared: number;
}

export interface RunRewardResult {
  coins: number;
  xp: number;
  loot: GearPiece[];
}

/** Fracción de la recompensa completa que se otorga en una derrota parcial. */
const PARTIAL_LOSS_FACTOR = 0.5;

/**
 * Calcula las recompensas de fin de run a partir de la definición del
 * capítulo y el resultado. No otorga nada si se pierde en la primera oleada
 * (0 oleadas superadas).
 */
export function computeRunRewards(
  chapter: ChapterDef,
  outcome: RunResultOutcome,
  wavesCleared: number,
  rng: Rng
): RunRewardResult {
  if (outcome === 'lost' && wavesCleared < 1) {
    return { coins: 0, xp: 0, loot: [] };
  }

  const totalWaves = Math.max(1, chapter.waves.length);
  const progressRatio = outcome === 'won' ? 1 : Math.min(1, wavesCleared / totalWaves);
  const factor = outcome === 'won' ? 1 : progressRatio * PARTIAL_LOSS_FACTOR;

  const coins = Math.round(chapter.rewards.coins * factor);
  const xp = Math.round(chapter.rewards.xp * factor);
  // Al perder solo se garantiza como máximo 1 pieza de gear (recompensa parcial "de consuelo").
  const gearDropCount = outcome === 'won' ? chapter.rewards.gearDrops : Math.min(1, chapter.rewards.gearDrops);
  const loot = rollGearDrops(gearDropCount, rng);

  return { coins, xp, loot };
}

/**
 * Aplica una recompensa ya calculada al estado meta: suma monedas/xp,
 * agrega el gear al inventario y, si se ganó, avanza el progreso del
 * capítulo. Se llama típicamente desde la escena Resultados antes de
 * guardar (`saveMetaState`).
 */
export function applyRunRewards(meta: MetaState, outcome: RunOutcome, reward: RunRewardResult): MetaState {
  let next = addCoins(meta, reward.coins);
  next = addXp(next, reward.xp);
  if (reward.loot.length > 0) {
    next = { ...next, gear: [...next.gear, ...reward.loot] };
  }
  if (outcome.outcome === 'won') {
    next = advanceChapterProgress(next, outcome.chapterIndex);
  }
  return next;
}
